import type { Bridge } from '@casa/bridge';
import { driverSecretKeys } from '@casa/drivers/config';
import { joinSecretsInText, placeholdersInText, splitSecretsInText } from '@casa/model';
import { deviceKeys } from '../secrets/device_keys.ts';
import {
    decryptSecret,
    encryptSecret,
    exportPublicKey,
    makeJoinKey,
    makeRecoveryKey,
    makeSpaceKey,
    parseJoinKey,
    parseRecoveryKey,
    recoveryKeyText,
    unwrapForDevice,
    unwrapForRecovery,
    wrapForDevice,
    wrapForRecovery,
    type Sealed,
    type WrappedForDevice,
} from '../secrets/keys.ts';
import { call, enterCloudSpace, isCloudKind, ServerError, listServerDashboards, readServerDashboard, readServerInfo, serverLink, setSpaceJoinKey, writeServerDashboard, type ServerLink } from './casa_server.ts';

// End-to-end credentials on tilez-cloud, the app's side (docs/specs/secrets.md SE-10 to SE-16,
// SE-21 to SE-23): a dashboard's credentials are sealed with the space's key before it is saved
// there, and opened after it is read, so tilez-cloud only ever keeps placeholders and sealed values.
// A device gets the space key wrapped for its own key pair: from the first device, which makes it;
// from any device that has it (QSE-4); or by the recovery key. Without it, a dashboard loads with its
// connections asking for their credentials (SE-23). Other servers are not touched.

export interface CloudDevice {
    id: string;
    name: string;
    publicKey: string;
    added: string;
    hasKey: boolean;
}

/** What tilez-cloud says about its space's keys (GET /api/keys). */
export interface CloudKeys {
    space: string;
    /** This device's id there. */
    me: string;
    /** 0 until the space has its key. */
    keyVersion: number;
    recovery: Sealed | null;
    devices: CloudDevice[];
    /** The space key wrapped for this device, once it was given it. */
    mine: WrappedForDevice | null;
    /** The join key (on screen, the space key), sealed with the space key, for the space's devices to show (cloud-spaces SP-3). */
    join: Sealed | null;
    /** Devices that had the key and were removed since it was made: it is to be renewed (SE-16). */
    removed: { name: string; at: string }[];
}

/** A space key this device has opened, by the server it is for. */
const opened = new Map<string, { space: string; keyVersion: number; key: CryptoKey }>();
const cloudServers = new Map<string, boolean>();

/** The credential fields of each driver: the built-in drivers' until the app says the installed ones' too. */
let secretKeysOf: (driver: string) => readonly string[] = driverSecretKeys;

/** The app's lookup of credential fields, the installed drivers' included (dashboard_app.tsx, once they are loaded). */
export function setSecretKeysOf(lookup: (driver: string) => readonly string[]) : void {
    secretKeysOf = lookup;
}

const serverOf = (link: ServerLink) : string => (link.kind === 'app' ? link.url : 'page');

/** The Tilez Cloud this device keeps credentials end-to-end on: its Tilez Cloud pairing (ST-10), else the server it is paired with, where that is a tilez-cloud. */
export async function cloudLink(bridge: Bridge) : Promise<ServerLink | undefined> {
    const cloud = await serverLink(bridge, 'cloud');
    if (cloud) {
        return cloud;
    }

    const server = await serverLink(bridge);

    return server && (await isCloudServer(bridge, server)) ? server : undefined;
}

/** Whether the server is a tilez-cloud: only those keep credentials end-to-end. A browser's page never is one here (cloud_page.tsx). */
export async function isCloudServer(bridge: Bridge, link: ServerLink) : Promise<boolean> {
    if (link.kind === 'page') {
        return false;
    }

    const known = cloudServers.get(serverOf(link));

    if (known !== undefined) {
        return known;
    }

    const isCloud = isCloudKind((await readServerInfo(bridge, link).catch(() => undefined))?.kind);

    cloudServers.set(serverOf(link), isCloud);

    return isCloud;
}

function failed(status: number, text: string, doing: string) : never {
    const message = (() => {
        try {
            return (JSON.parse(text) as { error?: string }).error;
        } catch {
            return undefined;
        }
    })();

    throw new ServerError(`${doing}: ${message ?? `the server answered ${status}`}`, status);
}

export async function readCloudKeys(bridge: Bridge, link: ServerLink) : Promise<CloudKeys> {
    const answer = await call(bridge, link, 'GET', '/api/keys');

    if (answer.status !== 200) {
        failed(answer.status, answer.text, 'Reading the keys failed');
    }

    return JSON.parse(answer.text) as CloudKeys;
}

async function send(bridge: Bridge, link: ServerLink, method: string, path: string, body: unknown, doing: string) : Promise<void> {
    const answer = await call(bridge, link, method, path, JSON.stringify(body), { 'content-type': 'application/json' });

    if (answer.status < 200 || answer.status > 299) {
        failed(answer.status, answer.text, doing);
    }
}

/** This device's public key, as the server keeps it. */
export async function myPublicKey() : Promise<string> {
    return exportPublicKey((await deviceKeys()).publicKey);
}

/** Says this device's public key to the server, unless it knows it (SE-12). */
async function register(bridge: Bridge, link: ServerLink, keys: CloudKeys) : Promise<void> {
    const publicKey = await myPublicKey();

    if (!keys.devices.some((device) => device.id === keys.me && device.publicKey === publicKey)) {
        await send(bridge, link, 'PUT', '/api/keys/device', { publicKey }, 'Saying this device\'s key failed');
    }
}

/**
 * The space key, if the server is a tilez-cloud whose space has one: `key` once this device has it.
 * Undefined for any other server, and for a tilez-cloud without a space key yet. Says this device's
 * key to the server on the way.
 */
export async function cloudSpaceKey(bridge: Bridge, link: ServerLink) : Promise<{ space: string; keyVersion: number; key: CryptoKey | undefined } | undefined> {
    if (!(await isCloudServer(bridge, link))) {
        return undefined;
    }

    const keys = await readCloudKeys(bridge, link);

    if (keys.keyVersion === 0) {
        return undefined;
    }

    const known = opened.get(serverOf(link));

    if (known && known.keyVersion === keys.keyVersion && known.space === keys.space) {
        return known;
    }

    await register(bridge, link, keys);

    if (!keys.mine) {
        return { space: keys.space, keyVersion: keys.keyVersion, key: undefined };
    }

    const key = await unwrapForDevice(keys.mine, await deviceKeys(), keys.space, keys.keyVersion);
    const space = { space: keys.space, keyVersion: keys.keyVersion, key };

    opened.set(serverOf(link), space);

    return space;
}

/** A dashboard read from the server, with its sealed credentials opened when this device can (SE-2, SE-23). */
export async function openCloudSecrets(bridge: Bridge, link: ServerLink, name: string, text: string) : Promise<string> {
    if (placeholdersInText(text).length === 0) {
        return text;
    }

    const space = await cloudSpaceKey(bridge, link);

    if (!space?.key) {
        return text;
    }

    const answer = await call(bridge, link, 'GET', `/api/secrets/${encodeURIComponent(name)}`);

    if (answer.status !== 200) {
        return text;
    }

    const sealed = JSON.parse(answer.text) as { keyVersion: number; values: Record<string, Sealed> };
    const values: Record<string, string> = {};

    for (const [secret, value] of Object.entries(sealed.values)) {
        try {
            values[secret] = await decryptSecret(space.key, value, { space: space.space, keyVersion: sealed.keyVersion, name: secret });
        } catch {
            // Sealed with another key, or changed: its connection asks for it, as a missing value.
        }
    }

    return joinSecretsInText(text, values);
}

/**
 * A dashboard about to be saved on the server, with its credentials sealed and saved apart, and
 * placeholders in their place (SE-10). As it is for any other server, or a tilez-cloud whose space
 * has no key yet (SE-21). Throws when this device would have to seal and has no key.
 */
export async function sealCloudSecrets(bridge: Bridge, link: ServerLink, name: string, text: string) : Promise<string> {
    const space = await cloudSpaceKey(bridge, link);

    if (!space) {
        return text;
    }

    const parts = splitSecretsInText(text, secretKeysOf);

    if (!parts || parts.placeholders.length === 0) {
        return text;
    }

    if (!space.key) {
        if (Object.keys(parts.values).length > 0) {
            throw new ServerError('Not saved: this device does not have the key for the credentials on Tilez Cloud yet. Approve it on a device that has it (Settings → Security), or use the recovery key.');
        }

        return text;
    }

    const answer = await call(bridge, link, 'GET', `/api/secrets/${encodeURIComponent(name)}`);
    const kept = answer.status === 200 ? (JSON.parse(answer.text) as { keyVersion: number; values: Record<string, Sealed> }) : undefined;
    const values: Record<string, Sealed> = {};

    for (const secret of parts.placeholders) {
        const value = parts.values[secret];

        if (value !== undefined) {
            values[secret] = await encryptSecret(space.key, value, { space: space.space, keyVersion: space.keyVersion, name: secret });
        } else if (kept?.keyVersion === space.keyVersion && kept.values[secret]) {
            values[secret] = kept.values[secret];
        }
    }

    await send(bridge, link, 'PUT', `/api/secrets/${encodeURIComponent(name)}`, { keyVersion: space.keyVersion, values }, `Saving the credentials of "${name}" failed`);

    return parts.text;
}

/** What a device shows once, after the keys were made: the space key to add devices, and the recovery key for when none is left. */
export interface NewKeys {
    joinKey: string;
    recoveryKey: string;
}

/** The context the join key is sealed in, beside the credentials. */
const JOIN_SECRET = 'space-key';

/**
 * Makes the space's key on the first device (SE-11, SE-14), and moves every dashboard's credentials
 * to it (SE-21). The join key lets a device into the space (cloud-spaces SP-3): kept by tilez-cloud as
 * a hash, and sealed for the space's devices to show. Gives the keys to be shown once.
 */
export async function setUpCloudSecrets(bridge: Bridge, link: ServerLink, joinKey: string = makeJoinKey()) : Promise<NewKeys & { moved: number }> {
    const keys = await readCloudKeys(bridge, link);

    if (keys.keyVersion !== 0) {
        throw new ServerError('The credentials on Tilez Cloud are encrypted already.');
    }

    await register(bridge, link, keys);

    const key = await makeSpaceKey();
    const recovery = makeRecoveryKey();

    await send(bridge, link, 'POST', '/api/keys/space', {
        wrapped: await wrapForDevice(key, await myPublicKey(), keys.space, 1),
        recovery: await wrapForRecovery(key, recovery, keys.space, 1),
        join: await encryptSecret(key, joinKey, { space: keys.space, keyVersion: 1, name: JOIN_SECRET }),
    }, 'Making the key failed');
    opened.set(serverOf(link), { space: keys.space, keyVersion: 1, key });
    await setSpaceJoinKey(bridge, link, joinKey);

    return { joinKey, recoveryKey: recoveryKeyText(recovery), moved: await moveCloudCredentials(bridge, link) };
}

/** Saves every dashboard on the server again, so that its credentials are sealed (SE-21); how many had any. */
export async function moveCloudCredentials(bridge: Bridge, link: ServerLink) : Promise<number> {
    let moved = 0;

    for (const entry of await listServerDashboards(bridge, link)) {
        const { text, version } = await readServerDashboard(bridge, link, entry.name);
        const parts = splitSecretsInText(text, secretKeysOf);

        if (parts && Object.keys(parts.values).length > 0) {
            await writeServerDashboard(bridge, link, entry.name, text, { version });
            moved += 1;
        }
    }

    return moved;
}

/** Gives the space key to a device waiting for it, after the user compared its code (SE-13, QSE-7). */
export async function approveCloudDevice(bridge: Bridge, link: ServerLink, device: CloudDevice) : Promise<void> {
    const space = await cloudSpaceKey(bridge, link);

    if (!space?.key) {
        throw new ServerError('This device does not have the key, so it cannot give it.');
    }

    const wrapped = await wrapForDevice(space.key, device.publicKey, space.space, space.keyVersion);

    await send(bridge, link, 'PUT', `/api/keys/devices/${encodeURIComponent(device.id)}`, { keyVersion: space.keyVersion, wrapped }, 'Giving the key failed');
}

/**
 * A new space key and a new recovery key (SE-16): after a device was removed, or when the recovery
 * key may be known. Every dashboard's credentials are sealed again with the new key, which the
 * devices that had the current one keep; a device removed keeps only what was saved before. Gives
 * the new keys, to be shown once: a new space key too, so that a removed device cannot join again.
 */
export async function renewCloudKey(bridge: Bridge, link: ServerLink) : Promise<NewKeys> {
    const space = await cloudSpaceKey(bridge, link);

    if (!space?.key) {
        throw new ServerError('This device does not have the key, so it cannot renew it.');
    }

    const keys = await readCloudKeys(bridge, link);
    const keyVersion = keys.keyVersion + 1;
    const key = await makeSpaceKey();
    const recovery = makeRecoveryKey();
    const joinKey = makeJoinKey();
    const devices: Record<string, WrappedForDevice> = {};

    for (const device of keys.devices.filter((each) => each.hasKey)) {
        devices[device.id] = await wrapForDevice(key, device.publicKey, keys.space, keyVersion);
    }

    const secrets: Record<string, { keyVersion: number; values: Record<string, Sealed> }> = {};

    for (const entry of await listServerDashboards(bridge, link)) {
        const answer = await call(bridge, link, 'GET', `/api/secrets/${encodeURIComponent(entry.name)}`);
        const sealed = answer.status === 200 ? (JSON.parse(answer.text) as { keyVersion: number; values: Record<string, Sealed> }) : undefined;

        if (!sealed || Object.keys(sealed.values).length === 0) {
            continue;
        }

        const values: Record<string, Sealed> = {};

        for (const [secret, value] of Object.entries(sealed.values)) {
            const plain = await decryptSecret(space.key, value, { space: keys.space, keyVersion: sealed.keyVersion, name: secret });

            values[secret] = await encryptSecret(key, plain, { space: keys.space, keyVersion, name: secret });
        }

        secrets[entry.name] = { keyVersion, values };
    }

    const join = await encryptSecret(key, joinKey, { space: keys.space, keyVersion, name: JOIN_SECRET });

    await send(bridge, link, 'POST', '/api/keys/renew', { keyVersion, devices, recovery: await wrapForRecovery(key, recovery, keys.space, keyVersion), join, secrets }, 'Renewing the key failed');
    opened.set(serverOf(link), { space: keys.space, keyVersion, key });
    // The old space key no longer lets a device in (cloud-spaces SP-6).
    await setSpaceJoinKey(bridge, link, joinKey);

    return { joinKey, recoveryKey: recoveryKeyText(recovery) };
}

/**
 * Starts a space on Tilez Cloud for this device (cloud-spaces SP-2, SP-3): its space key made here,
 * the credentials' key with a recovery key. Gives both keys, to be shown once.
 */
export async function startCloudSpace(bridge: Bridge, deviceName: string) : Promise<NewKeys> {
    const joinKey = makeJoinKey();

    await enterCloudSpace(bridge, 'start', joinKey, deviceName);

    const link = await serverLink(bridge, 'cloud');
    if (!link) {
        throw new ServerError('The space was started, but this device did not keep its pairing.');
    }

    const { recoveryKey } = await setUpCloudSecrets(bridge, link, joinKey);

    return { joinKey, recoveryKey };
}

/**
 * Joins the space whose space key was typed (SP-4). The credentials come after, from a device of the
 * space that approves this one, or with the recovery key (SE-13, SE-14).
 */
export async function joinCloudSpace(bridge: Bridge, typed: string, deviceName: string) : Promise<void> {
    const joinKey = parseJoinKey(typed);

    if (!joinKey) {
        throw new ServerError('That is not a home code: 8 letters and digits, as XXXX-XXXX.');
    }

    await enterCloudSpace(bridge, 'join', joinKey, deviceName);
    await sayDeviceKey(bridge);
}

/**
 * Says this device's key to its space on Tilez Cloud at once, after it joined (SE-12, SE-13): the
 * space's other devices then see it waiting, to approve it. A failure is only logged: the key is
 * said again whenever credentials are opened, or Security is shown.
 */
export async function sayDeviceKey(bridge: Bridge) : Promise<void> {
    try {
        const link = await serverLink(bridge, 'cloud');

        if (link) {
            await cloudSpaceKey(bridge, link);
        }
    } catch (error) {
        console.warn(`secrets: this device's key was not said yet: ${error instanceof Error ? error.message : String(error)}`);
    }
}

/** The space key, for a device of the space to show (SP-3); undefined when this device cannot open it. */
export async function spaceJoinKey(bridge: Bridge, link: ServerLink) : Promise<string | undefined> {
    const space = await cloudSpaceKey(bridge, link);
    const { join } = await readCloudKeys(bridge, link);

    if (!space?.key || !join) {
        return undefined;
    }

    try {
        return await decryptSecret(space.key, join, { space: space.space, keyVersion: space.keyVersion, name: JOIN_SECRET });
    } catch {
        return undefined;
    }
}

/** Opens the space key with the recovery key, and keeps it for this device (SE-14). */
export async function recoverCloudKey(bridge: Bridge, link: ServerLink, typed: string) : Promise<void> {
    const recovery = parseRecoveryKey(typed);

    if (!recovery) {
        throw new ServerError('That is not a recovery key: 26 letters and digits, in groups of four.');
    }

    const keys = await readCloudKeys(bridge, link);

    if (!keys.recovery) {
        throw new ServerError('The credentials on Tilez Cloud are not encrypted yet.');
    }

    let key: CryptoKey;

    try {
        key = await unwrapForRecovery(keys.recovery, recovery, keys.space, keys.keyVersion);
    } catch {
        throw new ServerError('That recovery key does not open the key of these credentials.');
    }

    await register(bridge, link, keys);

    const wrapped = await wrapForDevice(key, await myPublicKey(), keys.space, keys.keyVersion);

    await send(bridge, link, 'PUT', `/api/keys/devices/${encodeURIComponent(keys.me)}`, { keyVersion: keys.keyVersion, wrapped }, 'Keeping the key failed');
    opened.set(serverOf(link), { space: keys.space, keyVersion: keys.keyVersion, key });
}

